// src/app/components/admin/platform-settings-section.tsx
//
// The Platform section of Global Settings.
//
// It used to stack every platform switch on one page. That worked with two
// settings and stopped working at four — an admin looking for one of them had
// to scroll past the others, and each new switch made the page worse.
//
// So this is an index: a card per setting saying what it controls, and a
// detail view for the one you pick. The setting components themselves are
// untouched and still own their heading, copy and save behaviour — this shell
// only decides which one is on screen. Adding a fifth setting means adding one
// entry to SETTINGS below.

import { useState, type ComponentType, type CSSProperties, type ReactNode } from "react";
import { ArrowLeft, ChevronRight, FlaskConical, Gauge, Globe, KeyRound, Library, LifeBuoy, Link2, RefreshCw, ShieldCheck, SlidersHorizontal, UserPlus, Users } from "lucide-react";

import { SsoSettingsCard } from "@/app/components/sso-settings-card";
import { SsoConnectionsCard } from "@/app/components/sso-connections-card";
import { RegistrationSetting } from "./registration-setting";
import { PerformanceSourceSetting } from "./performance-source-setting";
import { ShareLinkExpirySetting } from "./share-link-expiry-setting";
import { EmailRecipientsSetting } from "./email-recipients-setting";
import { ProspectReadsSetting } from "./prospect-reads-setting";
import { ProviderThrottleSetting } from "./provider-throttle-setting";
import { AiModeBackupSetting } from "./aimode-backup-setting";
import { MeasurementDefaultsSetting } from "./measurement-defaults-setting";
import { PublicationRecheckSetting } from "./publication-recheck-setting";
import { TruthSetAutomationSetting } from "./truth-set-automation-setting";
import { FrameworkLibrarySetting } from "./framework-library-setting";
import { SmokeCalibrationSetting } from "./smoke-calibration-setting";

interface PlatformSetting {
  id: string;
  label: string;
  /** One line on the card: what this controls, in the admin's terms. */
  desc: string;
  icon: ComponentType<{ style?: CSSProperties; className?: string }>;
  color: string;
  render: () => ReactNode;
}

const SETTINGS: PlatformSetting[] = [
  {
    id: "email-recipients",
    label: "Approved email recipients",
    desc: "Which domains and addresses each brand is allowed to email.",
    icon: Globe,
    color: "var(--alpha-teal)",
    render: () => <EmailRecipientsSetting />,
  },
  {
    id: "registration",
    label: "Self-service signup",
    desc: "Whether people can create their own account, or an admin must.",
    icon: UserPlus,
    color: "var(--spectral-violet)",
    render: () => <RegistrationSetting />,
  },
  {
    id: "sso",
    label: "Single sign-on",
    desc: "Your identity provider, and each customer's own Okta or OIDC sign-in.",
    icon: ShieldCheck,
    color: "var(--alpha-teal)",
    render: () => (
      <div className="py-6 flex flex-col gap-6">
        <SsoConnectionsCard />
        <SsoSettingsCard />
      </div>
    ),
  },
  {
    id: "share-links",
    label: "Report share-link expiry",
    desc: "How long a public report link stays valid after it is created.",
    icon: Link2,
    color: "var(--signal-blue)",
    render: () => <ShareLinkExpirySetting />,
  },
  {
    id: "performance-source",
    label: "Performance data source",
    desc: "Whether performance charts read live data or the fixture.",
    icon: KeyRound,
    color: "var(--chart-4)",
    render: () => <PerformanceSourceSetting />,
  },
  {
    id: "prospect-reads",
    label: "Prospect reads",
    desc: "Whether a brand can be read at 3 repeats before they sign.",
    icon: Users,
    color: "var(--spectral-violet)",
    render: () => <ProspectReadsSetting />,
  },
  {
    id: "provider-throttle",
    label: "Provider call limits",
    desc: "Your Perplexity rate tier, and how hard measurement runs may call each provider.",
    icon: Gauge,
    color: "var(--signal-blue)",
    render: () => <ProviderThrottleSetting />,
  },
  {
    id: "aimode-backup",
    label: "Google AI Mode backup provider",
    desc: "SerpApi as a fallback when DataForSEO fails: key, on/off switch and cost per search.",
    icon: LifeBuoy,
    color: "var(--chart-4)",
    render: () => <AiModeBackupSetting />,
  },
  {
    id: "measurement-defaults",
    label: "Measurement defaults for new clients",
    desc: "Which model, or which Perplexity preset (Fast, Low, Medium…), a new client's first read runs on each lane. Clients already read don't change.",
    icon: SlidersHorizontal,
    color: "var(--signal-blue)",
    render: () => <MeasurementDefaultsSetting />,
  },
  {
    id: "publication-recheck",
    label: "Publication re-checks",
    desc: "The scheduled re-check of published content on the web-grounded lanes: on/off, delay, spend caps and limits.",
    icon: RefreshCw,
    color: "var(--alpha-teal)",
    render: () => <PublicationRecheckSetting />,
  },
  {
    id: "truth-set-automation",
    label: "Truth set automation",
    desc: "The off switch and spend caps for the truth set's automatic steps: scheduled site reads, fact extraction, and the answer check after each read.",
    icon: ShieldCheck,
    color: "var(--signal-blue)",
    render: () => <TruthSetAutomationSetting />,
  },
  {
    id: "framework-library",
    label: "Framework library",
    desc: "How many anonymised templates from approved frameworks Stage 2 is shown when it writes a new brand's framework. 0 turns it off.",
    icon: Library,
    color: "var(--spectral-violet)",
    render: () => <FrameworkLibrarySetting />,
  },
  {
    id: "smoke-calibration",
    label: "Smoke calibration",
    desc: "The off switches for calibration's automatic diagnosis and re-run, and the spend cap over one calibration's rounds.",
    icon: FlaskConical,
    color: "var(--alpha-teal)",
    render: () => <SmokeCalibrationSetting />,
  },
];

export function PlatformSettingsSection() {
  const [openId, setOpenId] = useState<string | null>(null);
  const open = SETTINGS.find((s) => s.id === openId) ?? null;

  if (open) {
    return (
      <div className="px-4 md:px-6">
        <button
          type="button"
          onClick={() => setOpenId(null)}
          className="mt-4 inline-flex items-center gap-1.5 rounded-lg px-2 py-1"
          style={{
            cursor: "pointer",
            background: "transparent",
            fontSize: "var(--text-sm)",
            color: "var(--muted-foreground)",
          }}
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          All platform settings
        </button>
        {open.render()}
      </div>
    );
  }

  return (
    <div className="px-4 md:px-6 py-6">
      <h2 style={{ fontSize: "var(--text-lg)", fontWeight: "var(--font-weight-semibold)", color: "var(--foreground)" }}>
        Platform
      </h2>
      <p className="mt-1" style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>
        Switches that apply to the whole deployment. Each takes effect immediately — no deploy,
        no restart.
      </p>

      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        {SETTINGS.map((setting) => {
          const Icon = setting.icon;
          return (
            <button
              key={setting.id}
              type="button"
              onClick={() => setOpenId(setting.id)}
              className="flex items-start gap-3 rounded-xl border p-4 text-left transition-all"
              style={{
                cursor: "pointer",
                borderColor: "var(--border)",
                backgroundColor: "var(--card, transparent)",
              }}
            >
              <span
                className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg"
                style={{ backgroundColor: "var(--muted, #f1f5f9)", color: setting.color }}
              >
                <Icon className="h-4.5 w-4.5" />
              </span>

              <span className="min-w-0 flex-1">
                <span
                  className="block"
                  style={{
                    fontSize: "var(--text-sm)",
                    fontWeight: "var(--font-weight-semibold)",
                    color: "var(--foreground)",
                  }}
                >
                  {setting.label}
                </span>
                <span
                  className="mt-0.5 block"
                  style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)", lineHeight: 1.5 }}
                >
                  {setting.desc}
                </span>
              </span>

              <ChevronRight
                className="h-4 w-4 flex-shrink-0"
                style={{ color: "var(--muted-foreground)" }}
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default PlatformSettingsSection;
